import { MailOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { Brand, Media } from '@omniafishing/core';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import bgPattern from '../assets/topo-blue-pattern.png';
import { BrandNotificationsModal } from '../components/brand_notifications_modal/brand_notifications_modal';
import BrandProductsGridContainer from '../components/brand_products_grid/brand_products_grid_container';
import OmniaCarousel from '../components/carousel/omnia_carousel';
import { ContentWrapper } from '../components/content_wrapper/content_wrapper';
import { MediaCardLegacy } from '../components/media_card_legacy/media_card_legacy';
import { OmniaBreadcrumb } from '../components/omnia_breadcrumb/omnia_breadcrumb';
import { OmniaButton } from '../components/omnia_button/omnia_button';
import { QuestionsCollapse } from '../components/questions_collapse/questions_collapse';
import { ScrollToTop } from '../components/scroll_to_top/scroll_to_top';
import { SEO } from '../components/seo/seo';
import { UserProfileCard } from '../components/user_profile_card/user_profile_card';
import { useUserPreferences } from '../hooks/use_user_preferences';
import { getImgixPath } from '../lib/imgix';
import { hasAccessToken } from '../redux/auth';
import { fetchBrand, getAmbassadors, unfavoriteBrand } from '../redux/brands';
import { getQas } from '../redux/pages';
import { RecommendationsActions } from '../redux/recommendations';
import { RoutePaths } from '../routes';
import base from '../styles/base.less';
import styles from './brand_page.less';

interface BrandPageProps {
  brand: Brand;
  media: Media[];
}

export const BrandPage = (props: BrandPageProps) => {
  const { brand, media } = props;

  const dispatch = useDispatch();
  const ambassadors = useSelector(getAmbassadors);
  const isLoggedIn = useSelector(hasAccessToken);
  const qas = useSelector(getQas);
  const { setUserPreferencesBrand } = useUserPreferences();

  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const openNotificationModal = () => {
    setNotificationModalVisible(true);
  };
  const closeNotificationModal = () => {
    setNotificationModalVisible(false);
  };

  useEffect(() => {
    setUserPreferencesBrand(brand.name);

    if (isLoggedIn) {
      dispatch(fetchBrand(brand.url_slug));
    }

    return () => {
      dispatch(RecommendationsActions.RECOMMENDATIONS_RESET());
    };
  }, []);

  const {
    image,
    description,
    meta_description,
    page_heading,
    display_name,
    meta_title,
    promo_banner,
    banner_image,
    name,
    user_favorite,
    url_slug,
    hidden,
  } = brand;
  const title = page_heading ? page_heading : display_name;

  const hasVideosArticles = !!media.length;

  return (
    <ScrollToTop>
      <section>
        <SEO
          description={meta_description}
          title={meta_title || `Shop for ${display_name}`}
          titleTemplate="%s"
        ></SEO>

        <header
          className={styles.header}
          style={{ backgroundImage: `url(${banner_image || bgPattern})` }}
        >
          <ContentWrapper>
            <div className={styles.blurb}>
              {image ? (
                <div className={styles.img}>
                  <h1 className={styles.imgH1}>
                    <img src={getImgixPath(image, { w: 600 })} alt={display_name} />
                  </h1>
                </div>
              ) : (
                <h1 className={classNames(base.h1, styles.h1)}>{title}</h1>
              )}
              {description && (
                <div
                  className={classNames(styles.description, 'mb-4')}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}

              {user_favorite ? (
                <div className="flex gap-3 justify-center flex-col sm:flex-row">
                  <OmniaButton
                    onPress={() => {
                      dispatch(unfavoriteBrand(url_slug));
                    }}
                    size="lg"
                    fontSize={14}
                    kind="secondary"
                  >
                    <StarFilled /> Unfollow {display_name}
                  </OmniaButton>
                  <OmniaButton
                    kind="tertiary"
                    fontSize={14}
                    onClick={openNotificationModal}
                    size="lg"
                  >
                    <MailOutlined /> Update Notifications
                  </OmniaButton>
                </div>
              ) : (
                <OmniaButton
                  className={styles.notificationsButton}
                  onClick={() => {
                    setNotificationModalVisible(true);
                  }}
                  kind="secondary"
                  size="lg"
                  fontSize={14}
                >
                  <StarOutlined /> Follow {display_name}
                </OmniaButton>
              )}
            </div>

            <OmniaBreadcrumb
              items={[
                {
                  name: 'Brands',
                  url: RoutePaths.BRANDS,
                },
                {
                  name: display_name,
                  url: null,
                },
              ]}
              className={styles.breadcrumb}
            />
          </ContentWrapper>
        </header>
        <section className={styles.products}>
          {hidden ? (
            <ContentWrapper>
              <p style={{ textAlign: 'center' }}>
                We don't currently stock products from this brand. <br />
                Click the follow button above and you'll be the first to know when we get products
                from them in stock.
              </p>
            </ContentWrapper>
          ) : (
            <BrandProductsGridContainer brand={brand} promoBanner={promo_banner} key={name} />
          )}
        </section>

        {hasVideosArticles && (
          <ContentWrapper>
            <section className={styles.section}>
              <h2>Videos &amp; Articles ({media.length})</h2>
              <ul className={styles.contentList}>
                {media.map((content) => {
                  return (
                    <li className={styles.content} key={content.uid}>
                      <MediaCardLegacy media={content} imgWidth={427} />
                    </li>
                  );
                })}
              </ul>
            </section>
          </ContentWrapper>
        )}

        {qas.length > 0 && (
          <ContentWrapper>
            <section className={styles.section}>
              <h2>{`Questions (${qas.length})`}</h2>
              <QuestionsCollapse qas={qas} />
            </section>
          </ContentWrapper>
        )}

        {ambassadors.length > 0 && (
          <ContentWrapper>
            <section className={styles.section}>
              <h2>{`Ambassadors (${ambassadors.length})`}</h2>
              <OmniaCarousel key={ambassadors.map((a) => a.slug).join()} arrows={true}>
                {ambassadors.map((profile) => (
                  <div className={styles.ambassador} key={profile.slug}>
                    <UserProfileCard profile={profile} />
                  </div>
                ))}
              </OmniaCarousel>
            </section>
          </ContentWrapper>
        )}
      </section>

      <BrandNotificationsModal
        brand={brand}
        favorite={brand.user_favorite}
        event_notifications={brand.user_event_notifications}
        open={notificationModalVisible}
        onClose={closeNotificationModal}
      />
    </ScrollToTop>
  );
};
